import React from 'react'

import { Dashboard, PageLayout, TabPills, useDashboardFeatureFlags } from '@components'
import { Dropdown, HeadContent, ToolbarButton, useMediaQueryContext } from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'
import { isWebView } from '@fourthwall/utils'
import { routing } from '@utils/routing'

import { AnalyticsLayout } from '../../analyticsModule/components/AnalyticsLayout/AnalyticsLayout'
import { AnalyticsToolbar } from '../../analyticsModule/components/AnalyticsToolbar/AnalyticsToolbar'
import { AnalyticsCompareDateRangeSelect } from '../../analyticsModule/components/tools/AnalyticsCompareDateRangeSelect/AnalyticsCompareDateRangeSelect'
import { AnalyticsDateRangeSelect } from '../../analyticsModule/components/tools/AnalyticsDateRangeSelect/AnalyticsDateRangeSelect'
import { AnalyticsPrecisionSelect } from '../../analyticsModule/components/tools/AnalyticsPrecisionSelect/AnalyticsPrecisionSelect'
import { useAnalyticsTabs } from '../../hooks/useAnalyticsTabs'
import { useAnalyticsParams } from '../../modules/AnalyticsParams/hooks/useAnalyticsParams'
import { GoogleAnalyticsBanner } from '../../modules/GoogleAnalytics/components/GoogleAnalyticsBanner/GoogleAnalyticsBanner'
import { GoogleAnalyticsBar } from '../../modules/GoogleAnalytics/components/GoogleAnalyticsBar/GoogleAnalyticsBar'
import { BottomContent } from './components/BottomContent/BottomContent'
import { DefaultAnalytics } from './modules/DefaultAnalytics/DefaultAnalytics'
import { MembershipsAnalytics } from './modules/MembershipsAnalytics/MembershipsAnalytics'
import { OverviewAnalytics } from './modules/OverviewAnalytics/OverviewAnalytics'
import { ShopAnalytics } from './modules/ShopAnalytics/ShopAnalytics'

export const AnalyticsDashboard = () => {
  const { areTabsEnabled, tabsOptions, activeTab, onTabChange } = useAnalyticsTabs()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const media = useMediaQueryContext()
  const { dateRange, compareDateRange, precision } = useAnalyticsParams()

  const renderContent = () => {
    if (!areTabsEnabled) {
      return <DefaultAnalytics />
    }

    switch (activeTab) {
      case 'overview':
        return <OverviewAnalytics />
      case 'shop':
        return <ShopAnalytics />
      case 'memberships':
        return <MembershipsAnalytics />
    }
  }

  const settings = [
    {
      enabled: dashboardFeatureFlags.analyticsTrackingPixels.enabled,
      label: 'Tracking pixels',
      to: routing.analyticsV2.overview.trackingPixels,
    },
  ].filter(({ enabled = true }) => enabled)

  const renderSettings = () => {
    if (isWebView() || !settings.length) return null

    return (
      <Dropdown
        content={settings.map((settingsItem, index) => (
          <Dropdown.Item key={index} {...settingsItem} />
        ))}
        position="bottom-end"
      >
        <ToolbarButton placement="top-end" label="Settings" icon={DotsHorizontalIcon} />
      </Dropdown>
    )
  }

  return (
    <>
      <HeadContent title="Analytics" titleTemplate={isWebView() ? 'Analytics' : undefined} />
      <PageLayout size="full">
        <Dashboard.Section
          toolbarContent={areTabsEnabled && media.tablet ? undefined : renderSettings()}
          name={isWebView() ? undefined : 'Analytics'}
          bottomContent={
            <BottomContent actions={areTabsEnabled && media.tablet ? renderSettings() : undefined}>
              {areTabsEnabled && (
                <TabPills
                  options={tabsOptions}
                  value={activeTab}
                  size="large"
                  onChange={onTabChange}
                />
              )}
            </BottomContent>
          }
        >
          <AnalyticsLayout
            toolbar={
              <AnalyticsToolbar bottomContent={<GoogleAnalyticsBanner />}>
                <AnalyticsDateRangeSelect {...dateRange} />
                {dashboardFeatureFlags.analyticsV2Compare.enabled && (
                  <AnalyticsCompareDateRangeSelect {...compareDateRange} />
                )}
                <AnalyticsPrecisionSelect {...precision} />
                <GoogleAnalyticsBar />
              </AnalyticsToolbar>
            }
          >
            {renderContent()}
          </AnalyticsLayout>
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
